import React, { useRef, Children, Component } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CheckIcon, XIcon, ShieldIcon, TrendingUpIcon, HeartIcon } from 'lucide-react';
export function AudienceSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const isForYou = ['Parents of children (ages 3-7) who eat fewer than 10 foods.', 'Moms and Dads exhausted by the 6 PM "Short-Order Cook" cycle.', 'Families where dinner has become a source of anxiety and arguments.', 'Parents who want to build a lifelong healthy relationship with food based on trust, not force.'];
  const notForYou = ['Parents looking for "hidden veggie" recipes or magic tricks.', 'Children with clinical medical dysphagia or severe sensory processing disorders requiring OT.', 'Anyone unwilling to commit to 7 days of consistent, neutral boundaries.'];
  const steps = [{
    number: 1,
    title: 'Identify the Anchors',
    description: 'Use our Safe Food Inventory to create a "Home Base" on every plate.',
    icon: ShieldIcon
  }, {
    number: 2,
    title: 'Climb the Ladder',
    description: 'Use the 6 rungs to turn a "scary" food into a neutral object through play and interaction.',
    icon: TrendingUpIcon
  }, {
    number: 3,
    title: 'Neutralize the Battle',
    description: 'Switch your language to our proven scripts to remove the emotion and the "No."',
    icon: HeartIcon
  }];
  return <section ref={ref} className="w-full bg-white py-10 md:py-16 px-4" data-id="element-1">
      <div className="max-w-3xl mx-auto" data-id="element-2">
        {/* Who This Is For / Not For */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4">
            {/* Is For You */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-[#F0F5EE] p-6 rounded-xl border border-[#8BA888]" data-id="element-5">
              <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold text-[#2D4A3E] mb-6" data-id="element-6">
                This Is For You If...
              </h3>
              <ul className="space-y-4" data-id="element-7">
                {isForYou.map((item, index) => <motion.li key={index} initial={{
                opacity: 0,
                x: -10
              }} animate={isInView ? {
                opacity: 1,
                x: 0
              } : {}} transition={{
                duration: 0.4,
                delay: 0.3 + index * 0.1
              }} className="flex items-start gap-3" data-id="element-8">
                    <div className="w-6 h-6 rounded-full bg-[#5B8A7A] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-9">
                      <CheckIcon className="w-4 h-4 text-white" data-id="element-10" />
                    </div>
                    <span className="font-['Source_Sans_3'] text-[#3A3A3A] leading-relaxed" data-id="element-11">
                      {item}
                    </span>
                  </motion.li>)}
              </ul>
            </motion.div>

            {/* Not For You */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-[#FFF8F0] p-6 rounded-xl border border-[#E8DDD0]" data-id="element-12">
              <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold text-[#2D4A3E] mb-6" data-id="element-13">
                This Is NOT For You If...
              </h3>
              <ul className="space-y-4" data-id="element-14">
                {notForYou.map((item, index) => <motion.li key={index} initial={{
                opacity: 0,
                x: 10
              }} animate={isInView ? {
                opacity: 1,
                x: 0
              } : {}} transition={{
                duration: 0.4,
                delay: 0.3 + index * 0.1
              }} className="flex items-start gap-3" data-id="element-15">
                    <div className="w-6 h-6 rounded-full bg-[#E8DDD0] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-16">
                      <XIcon className="w-4 h-4 text-[#3A3A3A]" data-id="element-17" />
                    </div>
                    <span className="font-['Source_Sans_3'] text-[#3A3A3A] leading-relaxed" data-id="element-18">
                      {item}
                    </span>
                  </motion.li>)}
              </ul>
            </motion.div>
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.4
      }} data-id="element-19">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl font-bold text-[#2D4A3E] mb-6 text-center" data-id="element-20">
            Three Simple Steps
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-21">
            {steps.map((step, index) => {
            const IconComponent = step.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.5 + index * 0.15
            }} className="bg-[#FFF8F0] p-6 rounded-xl border border-[#E8DDD0] text-center" data-id="element-22">
                  <div className="w-14 h-14 rounded-full bg-[#5B8A7A] flex items-center justify-center mx-auto mb-4" data-id="element-23">
                    <IconComponent className="w-7 h-7 text-white" data-id="element-24" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#2D4A3E] text-white font-bold flex items-center justify-center mx-auto mb-4 text-sm" data-id="element-25">
                    {step.number}
                  </div>
                  <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mb-2" data-id="element-26">
                    {step.title}
                  </h3>
                  <p className="font-['Source_Sans_3'] text-sm text-[#3A3A3A] leading-relaxed" data-id="element-27">
                    {step.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </motion.div>
      </div>
    </section>;
}